import PropTypes from 'prop-types';
import React from 'react';
import Cookies from 'universal-cookie';

import ComposableContext from '../ComposableContext';

/**
 * This Higher Order Component does the GraphQL query for the page. The reason that this is a HOC is
 * because we want SSR (Server Side Rendering) and prefetching when going to other pages. This gives
 * a better user experience.
 *
 * Most helpers, such as `getFeatureName` and `resizeImageSource` and container components are configurable.
 *
 * @param query                       The GraphQL query for this page.
 * @param isFound                     A helper function that determines if a page is found.
 * @param formatData                  A helper function to transform the result from GraphQL.
 * @param composableElementsConfig    The configuration for composable components.
 * @param getFeatureName              A helper function that returns the feature name of an element.
 * @param queryParametersToVariables  A helper function to transform query parameters to graphQL variables.
 * @param resizeImageSource           A helper function that resizes the image source in a URL, eg. {format} => 'w_500'.
 * @param groupComponent              A React component that groups page elements. Can be a div with a CSS grid for example.
 * @param contentContainerComponent   A React component that is used to wrap the content elements in for extra styling.
 * @param htmlHeadContainer           A React component that holds the <head> elements (meta data).
 */
const withComposablePage =
  ({
    query,
    isFound = () => true,
    formatData = (data) => data,
    composableElementsConfig,
    getFeatureName,
    queryParametersToVariables = (queryParameters) => queryParameters,
    resizeImageSource = (src) => src,
    groupComponent = React.Fragment,
    contentContainerComponent = React.Fragment,
    htmlHeadContainer = React.Fragment,
  }) =>
  (PageComponent) => {
    /**
     * The <WithComposablePageComponent /> will be wrapped around the original component.
     * It is basically just a context provider that holds all the configuration and data
     * for the composable page. The context can be used with `useComposablePage` in a
     * function component that is wrapped inside this HOC.
     */
    const WithComposablePageComponent = ({ composableData, ...pageProps }) => {
      const contextValue = {
        composableElementsConfig,
        resizeImageSource,
        getFeatureName,
        groupComponent,
        contentContainerComponent,
        htmlHeadContainer,
        isFound,
        data: composableData,
      };

      return (
        <ComposableContext.Provider value={contextValue}>
          <PageComponent {...pageProps} />
        </ComposableContext.Provider>
      );
    };

    WithComposablePageComponent.defaultProps = {
      composableData: null,
    };

    WithComposablePageComponent.propTypes = {
      composableData: PropTypes.shape({}),
    };

    WithComposablePageComponent.getInitialProps = async (ctx) => {
      const { apolloClient, query: queryParameters, res, req } = ctx;

      if (!apolloClient) {
        throw new Error(
          '`withComposablePage` needs to have an apolloClient in its context. Consider wrapping your Next.js page in a `withApolloClient` HOC.'
        );
      }

      let pageProps = {};

      if (PageComponent.getInitialProps) {
        pageProps = await PageComponent.getInitialProps(ctx);
      }

      const cookies = new Cookies(req ? req.headers.cookie : 'token');

      /**
       * Execute the GraphQL query.
       * The default `brandSlug` is 'portal'.
       */
      const result = await apolloClient
        .query({
          errorPolicy: 'ignore',
          query,
          variables: queryParametersToVariables({
            brandSlug: 'portal',
            token: cookies.get('token'),
            ...queryParameters,
          }),
        })
        // eslint-disable-next-line no-console
        .catch(console.error);

      if (!result?.data && res) {
        res.status(404);
        return pageProps;
      }

      // Format data using the helper function.
      const composableData = formatData(result.data);

      /**
       * If the page is not found and we are on Server Side (otherwise `res` would be undefined),
       * we can add a 404 status.
       */
      if (!isFound(composableData) && res) {
        res.status(404);
      }

      return {
        composableData,
        ...pageProps,
      };
    };

    return WithComposablePageComponent;
  };

export default withComposablePage;
